/**
 * ViewerToolbar — control bar rendered above the plan viewer canvas.
 *
 * Layout:
 *   [ViewerModeToggle]  ···  [ViewerVariantToggle] [CompassIndicator]
 *
 * Compass behaviour:
 *   - AXONOMETRIC mode: icon rotates live (driven by DOM writes from
 *     useAxonometricFrames via axonometricBridge.compassElRef — no React
 *     re-renders); click fires go-to-north via axonometricBridge.goToNorthRef.
 *   - Other modes: click toggles `rotatedToNorth` in ProductContext, which
 *     triggers usePlanNorthRotation in each plan renderer.
 */
import { useI18n }                from '../../../../shared/i18n/useI18n.js'
import { useProduct }               from '../../index.js'
import { PRODUCT_VIEW_MODE }        from '../../constants/viewModes.js'

import { getRotateToNorthAngleDeg } from '../../../../shared/utils/geometryUtils.js'

import { Button }   from '../../../../shared/ui/Button/index.js'
import { CompassNorth } from '../../../../shared/ui/icons/CompassNorth.jsx'
import { Tooltip }  from '../../../../shared/ui/Tooltip/index.js'

import { ViewerModeToggle }    from './ViewerModeToggle.jsx'
import { ViewerVariantToggle } from './ViewerVariantToggle.jsx'

import styles from './ViewerToolbar.module.scss'

// ── Compass indicator ──────────────────────────────────────────────────────────

/**
 * @param {number}   azimuth         — product.azimuth (degrees CCW from +X to geographic north)
 * @param {boolean}  isAxonometric   — true when axonometric mode is active
 * @param {object}   bridge          — axonometricBridge from ProductContext
 * @param {boolean}  rotatedToNorth  — current north-rotation state (non-axo only)
 * @param {function} onToggleNorth   — callback to toggle rotatedToNorth (non-axo only)
 * @param {string}   size            — "medium" or "large" (for button and icon sizing)
 */
function CompassIndicator({ 
  azimuth, 
  isAxonometric, 
  bridge, 
  rotatedToNorth, 
  onToggleNorth,
  size
 }) {
  const { t } = useI18n()

  if (isAxonometric) {
    // useAxonometricFrames writes rotate(deg)deg directly to bridge.compassElRef.current
    // via DOM manipulation on every frame change — zero React re-renders in the drag hot path.
    // Initial rotate(0deg) is overwritten by the hook's Effect 1 before the first paint settles.
    return (
      <Button
        type="button"
        kind="icon"
        size={size}
        title="Rotate to North"
        onClick={() => bridge.goToNorthRef.current?.()}
      >
        <CompassNorth
          ref={bridge.compassElRef}
          style={{ transform: 'rotate(-180deg)' }}
        />
      </Button>
    )
  }

  // Static: show where geographic north is in the current plan orientation.
  // When rotatedToNorth=false the icon rotates to point geographic north in the
  // plan's current orientation. When rotatedToNorth=true the plan has already
  // rotated so north is straight up — the icon shows rotate(0deg).
  const angleDeg = getRotateToNorthAngleDeg(azimuth ?? 90)
  const iconRotation = rotatedToNorth ? 0 : -angleDeg
  return (
    <Tooltip title={t('common.rotate_to_north')} placement='bottom' noWrap>
      <Button
        type="button"
        kind="icon"
        size={size}
        aria-label={t('common.rotate_to_north')}
        selected={rotatedToNorth}
        aria-pressed={rotatedToNorth}
        onClick={onToggleNorth}
      >
        <CompassNorth
          style={{ transform: `rotate(${iconRotation}deg)`, transition: 'transform 0.4s ease' }}
        />
      </Button>
    </Tooltip>
  )
}

// ── ViewerToolbar ──────────────────────────────────────────────────────────────

export function ViewerToolbar({ isMobile }) {
  const { selection, modes, viewerState, viewerActions, axonometricBridge } = useProduct()
  const azimuth       = selection.product?.azimuth ?? null
  const isAxonometric = modes.activeMode === PRODUCT_VIEW_MODE.AXONOMETRIC
  const { rotatedToNorth } = viewerState
  const { setRotatedToNorth } = viewerActions

  return (
    <div className={styles.toolbar}>
      <ViewerModeToggle isMobile={isMobile} />

      <div className={styles.right}>
        <ViewerVariantToggle isMobile={isMobile} />
        {azimuth !== null && (
          <CompassIndicator
            size={isMobile ? "medium" : "large"}
            azimuth={azimuth}
            isAxonometric={isAxonometric}
            bridge={axonometricBridge}
            rotatedToNorth={rotatedToNorth}
            onToggleNorth={() => setRotatedToNorth(!rotatedToNorth)}
          />
        )}
      </div>
    </div>
  )
}
